/*
<summary>
   This functional component is all about rendering the Borrower popup — showing the tool's
   basic info and submitting the Assign Tag Id via the Borrow Tool API.

   Every submit shows a confirmation popup first:
   - Assign Tag Id left blank: confirms using the tool without an assigned tag.
   - Assign Tag Id entered: confirms using the tool with that Tag Id.
   Developer: Aashish Singh, Created Date: 01-Sep-2026
</summary>
<param>No Parameter Passed</param>
<returns>Returns JSX</returns>
*/

import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Button, Modal } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import { useStore } from '../../../contexts/store-provider';
import { getUserId, getTenantIdFromLocalStorage } from '../../../helpers/localstorage-helper';
import { IBorrowToolRequest } from '../../../models/forms/IAddUpdateToolManagement';
import LoaderButton from '../../../shared-components/button/loader-button';
import { formatMessage } from '../../../translations/format-message';
import BorrowToolForm, { IBorrowToolFormValues } from '../forms/borrow-tool-form';

interface IProps {
  toolId: number;
  toolManagementNo: string;
  toolName: string;
  serialNo: string;
  tagId: string;
  currentTagId: string | null;
  modalClosed: () => void;
  onSuccess: () => void;
}

// 'no_tag': Assign Tag Id was left blank — confirms the tool will be used without an assigned
// tag. 'with_tag': Assign Tag Id was entered — confirms the tool will be used with that tag.
type IConfirmState = { type: 'no_tag' } | { type: 'with_tag'; tagId: string } | null;

/**
* This functional component renders the Borrower form and always confirms with the user
* before calling the Borrow Tool API — the confirmation message depends on whether an
* Assign Tag Id was entered.
* @param props: Tool identifier/details and modal close handler
*/
const BorrowTool = (props: IProps) => {
  const { toolManagementStore } = useStore();
  const { BorrowToolService, resetBorrowToolState, borrowToolState } = toolManagementStore;
  const [confirmState, setConfirmState] = useState<IConfirmState>(null);
  const [scanBuffer, setScanBuffer] = useState('');
  const [lastKeyTime, setLastKeyTime] = useState(Date.now());
  const [scannedTagId, setScannedTagId] = useState('');


  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!e.key) {
        return;
      }
      const currentTime = Date.now();
      const timeDiff = currentTime - lastKeyTime;
      if (
        e.key === 'Shift' ||
        e.key === 'Control' ||
        e.key === 'Alt' ||
        e.key === 'CapsLock' ||
        (e.key.length > 1 && e.key !== 'Enter')
      ) {
        return;
      }
      if (e.key === 'Enter') {
        if (timeDiff > 100) {
          setScanBuffer('');
          return;
        }
        const cleanedScan = scanBuffer.trim();

        if (cleanedScan) {
          setScannedTagId(cleanedScan);
          setScanBuffer('');
        }

        return;
      }

      if (timeDiff > 100) {
        setScanBuffer(e.key);
      } else {
        setScanBuffer((prev) => prev + e.key);
      }

      setLastKeyTime(currentTime);
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [scanBuffer, lastKeyTime]);

  useEffect(() => {
    if (borrowToolState.success) {
      toast.success(formatMessage('tool_borrowed_success'));
      resetBorrowToolState();
      setConfirmState(null);
      props.modalClosed();
      props.onSuccess();
    }
  }, [borrowToolState.success]);

  useEffect(() => {
    if (borrowToolState.error) {
      toast.error(formatMessage(borrowToolState.error));
      resetBorrowToolState();
    }
  }, [borrowToolState.error]);

  /**
  * This function is used to call the Borrow Tool API with the given Assigned Tag Id.
  * @param currentTagId: Tag Id to set as the tool's CurrentTagId
  */
  const submitBorrow = (currentTagId: string) => {
    const request: IBorrowToolRequest = {
      ToolId: props.toolId,
      ToolNumber: props.toolManagementNo,
      TagId: props.tagId,
      CurrentTagId: currentTagId,
      UpdatedBy: Number(getUserId()) || 0,
      TenantId: Number(getTenantIdFromLocalStorage()),
    };
    BorrowToolService(request);
  };

  /**
  * This function is used to submit the Borrower form — it always shows a confirmation popup,
  * with the message depending on whether an Assign Tag Id was entered.
  * @param values: Borrower form values
  */
  const onSubmitForm = (values: IBorrowToolFormValues) => {
    const assignTagId = values.AssignTagId?.trim();
    setConfirmState(assignTagId ? { type: 'with_tag', tagId: assignTagId } : { type: 'no_tag' });
  };

  const confirmHandler = () => {
    if (!confirmState) return;
    submitBorrow(confirmState.type === 'with_tag' ? confirmState.tagId : '');
  };

  return (
    <>
      <BorrowToolForm
        toolManagementNo={props.toolManagementNo}
        currentTagId={props.currentTagId}
        scannedTagId={scannedTagId}
        isLoading={borrowToolState.inProgress}
        onModalClosed={props.modalClosed}
        submitHandler={onSubmitForm}
      />
      {confirmState && (
        <Modal
          show={true}
          size='sm'
          centered
          dialogClassName="delete_popup status-change-user"
          className="borrow-confirm-modal"
          backdropClassName="borrow-confirm-backdrop"
        >
          <Modal.Header>
            <Modal.Title>
              <FormattedMessage id="borrow_the_tool" />
              <button
                onClick={() => setConfirmState(null)}
                disabled={borrowToolState.inProgress}
                className="Crossicons"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="23"
                  height="23"
                  fill="currentColor"
                  className="bi bi-x"
                  viewBox="0 0 16 16"
                >
                  <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
                </svg>
              </button>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="pt-0">
            <div className="bootbox-body">
              {confirmState.type === 'with_tag' ? (
                <FormattedMessage
                  id="confirm_use_with_tag_id"
                  values={{
                    tagId: confirmState.tagId,
                    b: (chunks: React.ReactNode) => <b>{chunks}</b>,
                  }}
                />
              ) : (
                <FormattedMessage id="confirm_use_without_tag_id" />
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="light-danger"
              onClick={() => setConfirmState(null)}
              disabled={borrowToolState.inProgress}
              className="btnOkSize"
            >
              <FormattedMessage id="cancel" />
            </Button>
            <LoaderButton
              type="submit"
              onClick={confirmHandler}
              isLoading={borrowToolState.inProgress}
              text="button_confirm"
            />
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
};

export default observer(BorrowTool);
